import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { facilitiesApi } from '../../../api/facilitiesApi';
import { institutionApi } from '../../../api/institutionsApi';
import { approvedClimateAssessments, facilityStatusUpdatesApi } from '../../../api/facilityStatusUpdatesApi';
import { Field, Select, ReadonlyBox, INP, INP_LOCKED } from '../../reusable/formRecords/formFieldPrimitives';
import FileUploadField from '../../shared/FileUploadField';
import { ASSESSMENT_SCOPE_OPTIONS, EMISSIONS_CLASSIFICATION_OPTIONS } from './climateOptions';

const today = () => new Date().toISOString().slice(0, 10);
const listOf = (result) => (Array.isArray(result?.items) ? result.items : (Array.isArray(result) ? result : []));

// "+ Propose" for the Activity Report's Facility Climate Assessment lookup:
// records an emissions-screening climate assessment (Facility Status Update,
// update_type = climate_assessment) on the activity's target facility. It is
// created as a Draft and can be linked once it has been approved.
export default function ProposeClimateAssessmentModal({ isOpen, onClose, onCreated, facilityUuid = '', activityUuid = '', activityTitle = '' }) {
  const { user } = useAuth();
  const blank = () => ({
    facility_uuid: facilityUuid, assessment_scope: 'facility_only', assessment_date: '', assessing_institution_uuid: '',
    emissions_classification: '', baseline_reference_uuid: '', assessment_report: '', assessment_report_file_name: '', notes: '',
  });
  const [form, setForm] = useState(blank);
  const [facilities, setFacilities] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return undefined;
    setForm(blank());
    setError('');
    setBusy(false);
    let active = true;
    facilitiesApi.listAll({ per_page: 500 }).then((r) => { if (active) setFacilities(listOf(r)); }).catch(() => {});
    institutionApi.list({ per_page: 200 }).then((r) => { if (active) setInstitutions(listOf(r)); }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const baselineOptions = useMemo(() => (isOpen ? approvedClimateAssessments({ assessmentType: 'ghg_baseline' }) : [])
    .sort((a, b) => Number(b.facility_uuid === form.facility_uuid) - Number(a.facility_uuid === form.facility_uuid))
    .map((row) => ({ value: row.uuid, label: [row.facility_name, row.assessment_date].filter(Boolean).join(' — ') })),
  [isOpen, form.facility_uuid]);

  if (!isOpen) return null;

  const facilityOptions = facilities.map((f) => ({ value: f.uuid ?? f.id, label: [f.name, f.facilityType, f.lga].filter(Boolean).join(' — ') }));
  const institutionOptions = institutions.map((i) => ({ value: i.uuid ?? i.id, label: i.name ?? i.uuid }));
  const facility = facilities.find((f) => (f.uuid ?? f.id) === form.facility_uuid);
  const set = (key) => (e) => { const value = e?.target ? e.target.value : e; setForm((prev) => ({ ...prev, [key]: value })); };
  const canCreate = form.facility_uuid && form.assessment_scope && form.assessment_date && form.assessment_date <= today()
    && form.assessing_institution_uuid && form.emissions_classification && form.baseline_reference_uuid && form.assessment_report;

  const submit = async () => {
    if (!canCreate || busy) return;
    setBusy(true);
    setError('');
    try {
      const record = await facilityStatusUpdatesApi.create({
        ...form,
        update_type: 'climate_assessment',
        assessment_type: 'emissions_screening',
        facility_name: facility?.name ?? '',
        facility_type: facility?.facility_type ?? '',
        state: facility?.state ?? '',
        lga: facility?.lga ?? '',
        assessing_institution_name: institutionOptions.find((o) => o.value === form.assessing_institution_uuid)?.label ?? '',
        linked_activity_uuid: activityUuid,
        linked_activity_title: activityTitle,
      }, user);
      onCreated?.(record);
      onClose();
    } catch (err) {
      setError(err?.message ?? 'Failed to propose the climate assessment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/50 px-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Propose Facility Climate Assessment</h3>
            <p className="mt-0.5 text-xs text-gray-400">Registry proposal — adds a Draft emissions screening to Facility Status Update</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-full text-xl leading-none text-gray-400 hover:bg-gray-100">×</button>
        </div>
        <div className="space-y-4 p-6">
          {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700">{error}</div>}
          <Field label="Facility" required hint={facilityUuid ? "The parent activity's target facility." : undefined}>
            <Select value={form.facility_uuid} onChange={set('facility_uuid')} options={facilityOptions} disabled={Boolean(facilityUuid)}
              placeholder="Select from registry" className={facilityUuid ? INP_LOCKED : INP} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Assessment Type" required>
              <ReadonlyBox>Emissions Screening</ReadonlyBox>
            </Field>
            <Field label="Assessment Scope" required>
              <Select value={form.assessment_scope} onChange={set('assessment_scope')} options={ASSESSMENT_SCOPE_OPTIONS} placeholder="Select scope" />
            </Field>
            <Field label="Assessment Date" required>
              <input type="date" max={today()} value={form.assessment_date} onChange={set('assessment_date')} className={INP} />
            </Field>
            <Field label="Assessing Institution" required>
              <Select value={form.assessing_institution_uuid} onChange={set('assessing_institution_uuid')} options={institutionOptions} placeholder="Select institution" />
            </Field>
            <Field label="Emissions Classification" required hint="Assigned by the assessor.">
              <Select value={form.emissions_classification} onChange={set('emissions_classification')} options={EMISSIONS_CLASSIFICATION_OPTIONS} placeholder="Select classification" />
            </Field>
            <Field label="Baseline Reference" required hint="An approved GHG baseline assessment.">
              <Select value={form.baseline_reference_uuid} onChange={set('baseline_reference_uuid')} options={baselineOptions}
                disabled={baselineOptions.length === 0} placeholder={baselineOptions.length ? 'Select approved baseline' : 'No approved GHG baseline yet'}
                className={baselineOptions.length ? INP : INP_LOCKED} />
            </Field>
          </div>
          <FileUploadField
            label="Assessment Report"
            required
            value={form.assessment_report}
            fileName={form.assessment_report_file_name}
            onChange={(dataUri, name) => setForm((prev) => ({ ...prev, assessment_report: dataUri, assessment_report_file_name: name }))}
            accept=".pdf"
            maxSizeMB={10}
            hint="PDF, max 10MB."
          />
          {activityTitle && (
            <Field label="Linked Activity"><ReadonlyBox>{activityTitle}</ReadonlyBox></Field>
          )}
          <p className="text-xs text-gray-500">
            Proposed as a Draft — it can be linked to this report once it has been submitted, validated and approved.
          </p>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="h-11 flex-1 rounded-full border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50">Close</button>
            <button type="button" onClick={submit} disabled={!canCreate || busy}
              className="h-11 flex-1 rounded-full bg-green-600 text-sm font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
              {busy ? 'Proposing…' : 'Propose'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
